import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import axios from 'axios';

const FeeReceiptDetails = () => {
  const { receiptId } = useParams();
  const [details, setDetails] = useState(null);
  const [school, setSchool] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    receiptNumber: "",
    date: "",
    description: "",
    paymentType: "cash",
    chequeDetails: null,
    paymentDetails: {
      paidMonths: [],
      feeHeads: []
    },
    creditAmount: 0
  });

  // 🔥 MONTH MAP
  const monthMap = {
    month1: "April",
    month2: "May",
    month3: "June",
    month4: "July",
    month5: "August",
    month6: "September",
    month7: "October",
    month8: "November",
    month9: "December",
    month10: "January",
    month11: "February",
    month12: "March",
  };

  const formatMonth = (monthKey) => monthMap[monthKey] || monthKey;

  // ---------------- FETCH SCHOOL ----------------
  useEffect(() => {
    const fetchSchool = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/school/getSchool`);
        if (Array.isArray(res.data) && res.data.length > 0) {
          setSchool(res.data[0]);
        }
      } catch (error) {
        console.error("Error fetching school details:", error);
      }
    };
    fetchSchool();
  }, []);

  // ---------------- FETCH RECEIPT ----------------
  useEffect(() => {
    const fetchReceiptDetails = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/ledgers/ledger/entry/${receiptId}`
        );
        setDetails(response.data);
      } catch (error) {
        console.error('Error fetching receipt details:', error);
        setDetails(null);
      }
    };

    if (receiptId) fetchReceiptDetails();
  }, [receiptId]);

  const paymentDetails = details?.paymentDetails || {};

  // ---------------- OPEN EDIT MODAL ----------------
  const openEditModal = () => {
    setEditData({
      receiptNumber: details.receiptNumber,
       date: details.date?.split("T")[0],
      description: details.description,
      paymentType: details.paymentType,
      chequeDetails: details.chequeDetails || null,
      paymentDetails: JSON.parse(JSON.stringify(details.paymentDetails)), // ✅ deep copy fix
      creditAmount: details.creditAmount
    });

    setShowEditModal(true);
  };

  const handleChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateFeeHeadAmount = (index, value) => {
    const updated = [...editData.paymentDetails.feeHeads];
    updated[index].amountPaid = Number(value);

    const total = updated.reduce((sum, f) => sum + f.amountPaid, 0);

    setEditData(prev => ({
      ...prev,
      paymentDetails: {
        ...prev.paymentDetails,
        feeHeads: updated
      },
      creditAmount: total
    }));
  };

  const submitEdit = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/ledgers/receipt/edit/${details.receiptNumber}`,
        editData
      );

      alert("Receipt updated");
      setShowEditModal(false);

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/ledgers/ledger/entry/${receiptId}`
      );

      setDetails(res.data);

    } catch (err) {
      console.error(err);
      alert( err.response?.data?.message||"Failed to update receipt");
    }
  };

  // ---------------- PDF ----------------
 const generatePDF = () => {

  if (!details || !school) {
    alert("Receipt not ready");
    return;
  }

  const pdf = new jsPDF("p", "mm", "a4");

  const receiptData = {
    receiptNo: details.receiptNumber,
    date: new Date(details.date).toLocaleDateString(),
    studentName: details.studentName,
    fatherName: details.fatherName,
    className: details.className,
    section: details.section,
    paymentType: details.paymentType,
    totalAmount: details.creditAmount,
    feeDetails: details.paymentDetails?.feeHeads?.map(f => ({
      name: monthMap[f.feeHeadName] || f.feeHeadName,
      amount: f.amountPaid
    })) || []
  };

  const printReceipt = (startX) => {
    let y = 8;

    // ================= SCHOOL HEADER =================
    pdf.setFontSize(11);
    pdf.setFont(undefined, "bold");
    pdf.text(school.schoolName || "", startX + 30, y, { align: "center" });
    y += 5;

    pdf.setFont(undefined, "normal");
    pdf.setFontSize(9);

    pdf.text(school.address || "", startX + 30, y, { align: "center" });
    y += 4;

    pdf.text(
      `School Code: ${school.schoolCode || ""}`,
      startX + 30,
      y,
      { align: "center" }
    );
    y += 4;

    pdf.text(
      `(M): ${school.phone1 || ""}    (O): ${school.phone2 || ""}`,
      startX + 30,
      y,
      { align: "center" }
    );

    y += 7;

    // ================= TITLE =================
    pdf.setFontSize(10);
    pdf.setFont(undefined, "bold");
    pdf.text("CASH RECEIPT", startX + 30, y, { align: "center" });

    y += 7;

    // ================= RECEIPT DETAILS =================
    pdf.setFont(undefined, "normal");
    pdf.setFontSize(9);

    pdf.text(`Date: ${receiptData.date}`, startX, y);
    pdf.text(`Receipt No: ${receiptData.receiptNo}`, startX + 35, y);
    y += 6;

    pdf.text(`Name: ${receiptData.studentName}`, startX, y);
    y += 5;

    pdf.text(`S/D/O: ${receiptData.fatherName}`, startX, y);
    y += 5;

    pdf.text(
      `Class: ${receiptData.className} (${receiptData.section})`,
      startX,
      y
    );

    y += 6;

    // ================= TABLE HEADER =================
    pdf.line(startX, y, startX + 60, y);
    y += 4;

    pdf.setFont(undefined, "bold");
    pdf.text("Fee Head", startX, y);
    pdf.text("Amount", startX + 45, y);
    y += 4;

    pdf.setFont(undefined, "normal");

    // ================= FEE DETAILS =================
    receiptData.feeDetails.forEach(fee => {
  pdf.text(fee.name.toUpperCase(), startX, y);

  pdf.setCharSpace(0); // ⭐ FIX
  pdf.text(`Rs. ${fee.amount}`, startX + 45, y);

  y += 5;
});

    // ================= TOTAL =================
    pdf.line(startX, y, startX + 60, y);
    y += 6;

    pdf.setFont(undefined, "bold");
    pdf.text("Total", startX, y);
pdf.setCharSpace(0); // ⭐ FIX
pdf.text(`Rs. ${receiptData.totalAmount}`, startX + 45, y);
    y += 6;

    // ================= PAYMENT MODE =================
    pdf.setFont(undefined, "normal");
    pdf.text("Pay Mode", startX, y);
    pdf.text(receiptData.paymentType?.toUpperCase(), startX + 45, y);

    y += 10;

    // ================= FOOTER =================
    pdf.setFont(undefined, "bold");
    pdf.text(`For ${school.schoolName}`, startX + 30, y, { align: "center" });
    y += 6;

    pdf.setFont(undefined, "normal");
    pdf.setFontSize(8);
    pdf.text(
      "This is computer generated receipt. No signature required.",
      startX + 30,
      y,
      { align: "center" }
    );
  };

  // LEFT COPY → School Copy
  printReceipt(10);

  // RIGHT COPY → Parent Copy
  printReceipt(110);

  // CENTER DIVIDER
  pdf.line(105, 5, 105, 290);

  pdf.save(`Receipt_${receiptData.receiptNo}.pdf`);
};

const printPage = () => {

  if (!details || !school) {
    alert("Receipt not ready");
    return;
  }

  const pdf = new jsPDF("p", "mm", "a4");

  const receiptData = {
    receiptNo: details.receiptNumber,
    date: new Date(details.date).toLocaleDateString(),
    studentName: details.studentName,
    fatherName: details.fatherName,
    className: details.className,
    section: details.section,
    paymentType: details.paymentType,
    totalAmount: details.creditAmount,
    feeDetails: details.paymentDetails?.feeHeads?.map(f => ({
      name: monthMap[f.feeHeadName] || f.feeHeadName,
      amount: f.amountPaid
    })) || []
  };
const getFeeName = (name) => {
  const monthKey = name?.match(/month\d+/)?.[0];
  const month = monthMap[monthKey];

  if (name?.includes("route_")) {
    return `Bus Fee - ${month}`;
  }

  return month || name;
};
  const printReceipt = (startX) => {
    let y = 8;

    pdf.setFontSize(11);
    pdf.setFont(undefined, "bold");
    pdf.text(school.schoolName || "", startX + 30, y, { align: "center" });
    y += 5;

    pdf.setFont(undefined, "normal");
    pdf.setFontSize(9);

    pdf.text(school.address || "", startX + 30, y, { align: "center" });
    y += 4;

    pdf.text(`School Code: ${school.schoolCode || ""}`, startX + 30, y, { align: "center" });
    y += 4;

    pdf.text(`(M): ${school.phone1 || ""}    (O): ${school.phone2 || ""}`, startX + 30, y, { align: "center" });
    y += 7;

    pdf.setFontSize(10);
    pdf.setFont(undefined, "bold");
    pdf.text("CASH RECEIPT", startX + 30, y, { align: "center" });

    y += 7;

    pdf.setFont(undefined, "normal");
    pdf.setFontSize(9);

    pdf.text(`Date: ${receiptData.date}`, startX, y);
    pdf.text(`Receipt No: ${receiptData.receiptNo}`, startX + 35, y);
    y += 6;

    pdf.text(`Name: ${receiptData.studentName}`, startX, y);
    y += 5;

    pdf.text(`S/D/O: ${receiptData.fatherName}`, startX, y);
    y += 5;

    pdf.text(`Class: ${receiptData.className} (${receiptData.section})`, startX, y);
    y += 6;

    pdf.line(startX, y, startX + 60, y);
    y += 4;

    pdf.setFont(undefined, "bold");
    pdf.text("Fee Head", startX, y);
    pdf.text("Amount", startX + 45, y);
    y += 4;

    pdf.setFont(undefined, "normal");

receiptData.feeDetails.forEach(fee => {

  const feeName = getFeeName(fee.name);

  pdf.text(feeName.toUpperCase(), startX, y);

  pdf.setCharSpace(0);
  pdf.text(`Rs. ${fee.amount}`, startX + 45, y);

  y += 5;

});

    pdf.line(startX, y, startX + 60, y);
    y += 6;

    pdf.setFont(undefined, "bold");
    pdf.text("Total", startX, y);
    pdf.setCharSpace(0); // FIX
pdf.text(`Rs. ${receiptData.totalAmount}`, startX + 45, y);
    y += 6;

    pdf.setFont(undefined, "normal");
    pdf.text("Pay Mode", startX, y);
    pdf.text(receiptData.paymentType?.toUpperCase(), startX + 45, y);

    y += 10;

    pdf.setFont(undefined, "bold");
    pdf.text(`For ${school.schoolName}`, startX + 30, y, { align: "center" });
    y += 6;

    pdf.setFont(undefined, "normal");
    pdf.setFontSize(8);
    pdf.text(
      "This is computer generated receipt. No signature required.",
      startX + 30,
      y,
      { align: "center" }
    );
  };

  // Two copies
  printReceipt(10);
  printReceipt(110);
  pdf.line(105, 5, 105, 290);

  // 🔥 SAME PAGE PRINT (NO TAB, NO URL CHANGE)

  const blob = pdf.output("blob");
  const blobUrl = URL.createObjectURL(blob);

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";

  iframe.src = blobUrl;

  document.body.appendChild(iframe);

  iframe.onload = function () {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(blobUrl);
    }, 1000);
  };
};
const getFeeName = (name) => {
  const monthKey = name?.match(/month\d+/)?.[0];
  const month = monthMap[monthKey];

  if (name?.includes("route_")) {
    return `Bus Fee - ${month}`;
  }

  return month || name;
};
  if (!details) {
    return (
      <div className="w-full max-w-full p-6 bg-white shadow-lg rounded-lg">
        <p className="text-gray-600">Receipt not found</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full p-6 bg-white shadow-lg rounded-lg" id="receipt">

      {/* SCHOOL HEADER */}
      <div className="text-center mb-6">
        {school?.logo && (
          <img src={school.logo} alt="School Logo" className="mx-auto mb-2" style={{ height: "60px" }} />
        )}
        <h1 className="text-3xl font-extrabold text-gray-800 mb-1">
          {school?.schoolName}
        </h1>
        <p className="text-gray-600 text-sm">{school?.address}</p>
      </div>

      <p className="text-center italic mb-6">
        *This is a computer-generated slip of fees paid*
      </p>

      {/* RECEIPT DETAILS */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Receipt Number</h2>
        <p>{details.receiptNumber}</p>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Payment Date</h2>
        <p>{new Date(details.date).toLocaleDateString()}</p>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Description</h2>
        <p>{details.description}</p>
      </div>

      {/* FEE DETAILS */}
      <div>
        <h2 className="text-2xl font-semibold mb-4">Fee Details</h2>

        <table className="w-full border">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 border">Paid For</th>
              <th className="p-3 border">Amount</th>
            </tr>
          </thead>

          <tbody>
           {paymentDetails?.feeHeads?.map((fee, i) => (
  <tr key={i}>
    <td className="p-3 border">
      {getFeeName(fee.feeHeadName)}
    </td>
    <td className="p-3 border">
      ₹{fee.amountPaid}
    </td>
  </tr>
))}

            <tr className="font-bold bg-gray-50">
              <td className="p-3 border">Total Paid</td>
              <td className="p-3 border">
                ₹{details.creditAmount}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* BUTTONS */}
      <div className="mt-6 flex justify-between">
        <button
          onClick={generatePDF}
          className="px-4 py-2 bg-blue-600 text-white rounded"
        >
          Generate PDF
        </button>

        <button
          onClick={openEditModal}
          className="px-4 py-2 bg-yellow-600 text-white rounded"
        >
          Edit Receipt
        </button>

        <button
          onClick={printPage}
          className="px-4 py-2 bg-green-600 text-white rounded"
        >
          Print
        </button>
      </div>

      {/* EDIT MODAL */}
      {showEditModal && (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 px-4">
    
    <div className="bg-white p-6 rounded w-[600px] max-w-full max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-4">Edit Receipt</h2>
            <label>Receipt Number</label>
<input
  value={editData.receiptNumber}
  onChange={(e) => handleChange("receiptNumber", e.target.value)}
  className="w-full border p-2 mb-3"
/>


            <label>Date</label>
<input
  type="date"
  value={editData.date}
  onChange={(e) => handleChange("date", e.target.value)}
  className="w-full border p-2 mb-3"
/>

            <label>Description</label>
            <input
              value={editData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              className="w-full border p-2 mb-3"
            />

            <label>Payment Type</label>
            <select
              value={editData.paymentType}
              onChange={(e) => handleChange("paymentType", e.target.value)}
              className="w-full border p-2 mb-3"
            >
              <option value="cash">Cash</option>
              <option value="cheque">Cheque</option>
              <option value="upi">UPI</option>
            </select>

            {/* Cheque Fields */}
            {editData.paymentType === "cheque" && (
              <>
                <label>Cheque Number</label>
                <input
                  value={editData.chequeDetails?.chequeNumber || ""}
                  onChange={(e) =>
                    setEditData(prev => ({
                      ...prev,
                      chequeDetails: {
                        ...prev.chequeDetails,
                        chequeNumber: e.target.value
                      }
                    }))
                  }
                  className="w-full border p-2 mb-3"
                />

                <label>Bank Name</label>
                <input
                  value={editData.chequeDetails?.bankName || ""}
                  onChange={(e) =>
                    setEditData(prev => ({
                      ...prev,
                      chequeDetails: {
                        ...prev.chequeDetails,
                        bankName: e.target.value
                      }
                    }))
                  }
                  className="w-full border p-2 mb-3"
                />
              </>
            )}

            <h3 className="font-semibold mt-4 mb-2">Fee Heads</h3>

            {editData.paymentDetails?.feeHeads?.map((fee, i) => (
              <div key={i} className="flex justify-between mb-2">
                <span>
  {
    monthMap[
      fee.feeHeadName
        ?.toLowerCase()
        ?.replace(/\s/g, "")
    ] || fee.feeHeadName
  }
</span>
                <input
                  type="number"
                  value={fee.amountPaid}
                  onChange={(e) => updateFeeHeadAmount(i, e.target.value)}
                  className="border p-1 w-[120px]"
                />
              </div>
            ))}

            <div className="font-bold mt-3">
              Total: ₹{editData.creditAmount}
            </div>

            <div className="flex justify-end mt-5 gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-400 text-white rounded"
              >
                Cancel
              </button>

              <button
                onClick={submitEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Save Changes
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default FeeReceiptDetails;